import React, { useState } from 'react';
import {
  Grid,
  TextField,
  Box,
  Typography,
  InputAdornment,
  IconButton
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ChevronRight } from '@mui/icons-material';
import { SearchIcon } from '@/common/icons/SearchIcon';
import ClearIcon from '@mui/icons-material/Clear';
import { LoadingButton } from '@mui/lab';
import AuthUserStore from '@/common/utils/AuthUser';

const useStyles = makeStyles(theme => ({
  button: {
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    backgroundColor: 'white',
    border: '1px solid #dadada',
    '&:hover': {
      backgroundColor: '#f5f5f5',
      border: '1px solid #dadada'
    },
    '&.Mui-disabled': {
      backgroundColor: '#f8f8f8'
    }
  },
  allstudios: {
    maxHeight: 300,
    overflowY: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  },
  searchIcon: {
    color: '#999999'
  },
  clearSearch: {
    padding: 0.75,
    marginRight: '0px'
  },
  descriptionText: {
    color: '#000000',
    fontSize: '15px',
    fontWeight: 400,
    lineHeight: '22px'
  },
  storeCodeText: {
    fontSize: '15px',
    fontWeight: 400,
    lineHeight: '22px',
    color: '#999999'
  },
  select: {
    color: '#000000'
  }
}));

export const MAX_DISPLAY_OPTIONS = 3;

export type StoreType = {
  description: string;
  roleName?: string;
  storeCode: string;
  id: number;
};

type Props = {
  data: StoreType[];
  onChange: (storeId: number) => void;
  condensed?: boolean;
  loading?: boolean;
};

export const LoginSelectStores = ({
  onChange,
  data,
  condensed = false,
  loading
}: Props) => {
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudio, setSelectedStudio] = useState<number | null>(null);

  const currentStoreId = AuthUserStore.getStoreId();

  const handleStudioSelect = (storeId: number) => {
    setSelectedStudio(storeId);
    onChange(storeId);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const filteredData = data.filter(
    store =>
      store.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      store.storeCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayedOptions = condensed
    ? filteredData.slice(0, MAX_DISPLAY_OPTIONS)
    : filteredData;

  return (
    <Grid container spacing={1.5}>
      {condensed ? (
        displayedOptions.map(store => (
          <Grid item xs={12} key={store.id}>
            <LoadingButton
              fullWidth
              variant="outlined"
              className={classes.button}
              onClick={() => handleStudioSelect(store.id)}
              disabled={!!selectedStudio || currentStoreId === Number(store.id)}
              loading={loading && selectedStudio === store.id}
            >
              <Box textAlign="left">
                <Typography className={classes.descriptionText}>
                  {store.description}
                </Typography>
                <Typography className={classes.storeCodeText}>
                  {store.storeCode}
                </Typography>
              </Box>
              <ChevronRight className={classes.select} />
            </LoadingButton>
          </Grid>
        ))
      ) : (
        <Grid item xs={12}>
          <TextField
            fullWidth
            placeholder="Search Studios..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ marginBottom: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment className={classes.searchIcon} position="start">
                  <SearchIcon stroke={'#999999'} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearSearch}
                    className={classes.clearSearch}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Box className={classes.allstudios}>
            {filteredData.map(store => (
              <Box key={store.id} mb={1.5}>
                <LoadingButton
                  fullWidth
                  variant="outlined"
                  className={classes.button}
                  onClick={() => handleStudioSelect(store.id)}
                  disabled={
                    !!selectedStudio || currentStoreId === Number(store.id)
                  }
                  loading={loading && selectedStudio === store.id}
                  disableRipple
                >
                  <Box textAlign="left">
                    <Typography className={classes.descriptionText}>
                      {store.description}
                    </Typography>
                    <Typography className={classes.storeCodeText}>
                      {store.storeCode}
                    </Typography>
                  </Box>
                  <ChevronRight className={classes.select} />
                </LoadingButton>
              </Box>
            ))}
          </Box>
        </Grid>
      )}
    </Grid>
  );
};
